import { useEffect, useMemo } from "react";
import {
	getGetPaymentProductConfigurationQueryKey,
	getPaymentProductConfiguration,
	GetPaymentProductConfigurationQueryError,
	GetPaymentProductConfigurationState,
	PaymentProductConfigurationResponse,
	PaymentProductConfigurationResponseItemsItem,
	useGetPaymentProductConfiguration as useApiClientGetPaymentProductConfiguration,
} from "@getbread/api-client-program-v2";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";

import { getRequestConfig } from "merchant-portal-data/request";
import { PaginationParams } from "merchant-portal-data/types";

interface UseGetPaymentProductConfigurationParams extends PaginationParams {
	programID: string;
	merchantID: string;
	state?: GetPaymentProductConfigurationState;
}

export const useGetPaymentProductConfiguration = ({
	programID,
	merchantID,
	state,
	limit,
	offset,
}: UseGetPaymentProductConfigurationParams) => {
	return useApiClientGetPaymentProductConfiguration(
		programID,
		{
			"merchantID.eq": merchantID,
			state,
			limit,
			offset,
		},
		{ ...getRequestConfig() },
	);
};

// Hook to get payment product configurations for multiple programs
interface UseGetPaymentProductConfigurationsForProgramsParams extends PaginationParams {
	programIDs: string[];
	merchantID: string;
	state?: GetPaymentProductConfigurationState;
}

export const useGetPaymentProductConfigurationsForPrograms = ({
	programIDs,
	merchantID,
	state,
	limit,
	offset,
}: UseGetPaymentProductConfigurationsForProgramsParams) => {
	const enabled = useMemo(
		() => programIDs.length > 0 && !!merchantID,
		[programIDs.length, merchantID],
	);

	const results = useQueries({
		queries: programIDs.map((id) => ({
			enabled,
			queryKey: getGetPaymentProductConfigurationQueryKey(id, {
				"merchantID.eq": merchantID,
				state,
				limit,
				offset,
			}),
			queryFn: async () =>
				getPaymentProductConfiguration(
					id,
					{
						"merchantID.eq": merchantID,
						state,
						limit,
						offset,
					},
					getRequestConfig().request,
				),
		})),
		combine: (r) => ({
			data: r.map((result) => {
				// get the  result?.data?.data?.items and flatten it
				return result?.data?.data?.items;
			}),
		}),
	});
	return results;
};

interface UseGetAllPaymentProductConfigurationsForProgramsParams {
	programIDs: string[];
	merchantID: string;
}

interface PaymentProductConfigurationResponsePagination {
	total: number;
	offset: number;
	limit: number;
}

// Extended type that includes pagination field returned by API but not in the interface
interface PaymentProductConfigurationResponseWithPagination extends PaymentProductConfigurationResponse {
	pagination: PaymentProductConfigurationResponsePagination;
}

interface CombinedPaymentProductConfigurationPage {
	items: PaymentProductConfigurationResponseItemsItem[];
	pagination: PaymentProductConfigurationResponsePagination;
}

const limit = 200;

function isPaginationParam(param: unknown): param is { limit: number; offset: number } {
	return (
		typeof param === "object" &&
		param !== null &&
		"limit" in param &&
		"offset" in param &&
		typeof param.limit === "number" &&
		typeof param.offset === "number"
	);
}

/**
 * Fetches payment product configurations for multiple programs and combines the results
 */
async function fetchCombinedPaymentProductConfigurations(
	programIDs: string[],
	merchantID: string,
	state: GetPaymentProductConfigurationState,
	offset: number,
): Promise<CombinedPaymentProductConfigurationPage> {
	const responses = await Promise.all(
		programIDs.map((programID) =>
			getPaymentProductConfiguration(
				programID,
				{
					"merchantID.eq": merchantID,
					"state": state,
					"limit": limit,
					"offset": offset,
				},
				getRequestConfig().request,
			),
		),
	);

	// Combine all responses
	const combinedItems = responses.flatMap(
		(response) =>
			(response.data as PaymentProductConfigurationResponseWithPagination)?.items ?? [],
	);

	// Find the maximum total across all programs
	const allPaginations = responses
		.map((r) => (r.data as PaymentProductConfigurationResponseWithPagination)?.pagination)
		.filter(Boolean);

	const maxTotal = Math.max(...allPaginations.map((p) => p.total), 0);

	return {
		items: combinedItems,
		pagination: {
			total: maxTotal,
			offset: offset,
			limit: limit,
		},
	};
}

/**
 * Determines the next page parameters for pagination
 */
function getNextPageParam(
	lastPage: CombinedPaymentProductConfigurationPage | undefined,
): { limit: number; offset: number } | undefined {
	if (!lastPage?.pagination) return;

	const { total, offset } = lastPage.pagination;

	if (offset + limit >= total) return;

	return {
		limit: limit,
		offset: offset + limit,
	};
}

/**
 * Infinite query configuration for fetching payment product configurations by state
 */
function createPaymentProductConfigurationQuery(
	merchantID: string,
	sortedProgramIDs: string[],
	programIDs: string[],
	state: GetPaymentProductConfigurationState,
	enabled: boolean,
) {
	return {
		queryKey: ["payment-product-configurations-all", merchantID, sortedProgramIDs, state],
		queryFn: async ({ pageParam }: { pageParam: unknown }) => {
			if (!isPaginationParam(pageParam)) {
				throw new Error("Invalid pagination parameters");
			}

			return fetchCombinedPaymentProductConfigurations(
				programIDs,
				merchantID,
				state,
				pageParam.offset,
			);
		},
		enabled,
		getNextPageParam,
		initialPageParam: {
			limit: limit,
			offset: 0,
		},
	};
}

/**
 * Hook that retrieves all combines states of ACTIVE and LAPSED.
 */
export const useGetAllPaymentProductConfigurationsForPrograms = ({
	programIDs,
	merchantID,
}: UseGetAllPaymentProductConfigurationsForProgramsParams) => {
	const enabled = useMemo(
		() => programIDs.length > 0 && !!merchantID,
		[programIDs.length, merchantID],
	);

	const sortedProgramIDs = useMemo(
		() => [...programIDs].sort((a, b) => a.localeCompare(b)),
		[programIDs],
	);

	// Get ACTIVE configurations
	const {
		data: activeData,
		isFetchingNextPage: isFetchingActiveNextPage,
		fetchNextPage: fetchActiveNextPage,
		hasNextPage: hasActiveNextPage,
		isLoading: isLoadingActive,
	} = useInfiniteQuery<
		CombinedPaymentProductConfigurationPage,
		GetPaymentProductConfigurationQueryError
	>(
		createPaymentProductConfigurationQuery(
			merchantID,
			sortedProgramIDs,
			programIDs,
			GetPaymentProductConfigurationState.ACTIVE,
			enabled,
		),
	);

	// Get LAPSED configurations
	const {
		data: lapsedData,
		isFetchingNextPage: isFetchingLapsedNextPage,
		fetchNextPage: fetchLapsedNextPage,
		hasNextPage: hasLapsedNextPage,
		isLoading: isLoadingLapsed,
	} = useInfiniteQuery<
		CombinedPaymentProductConfigurationPage,
		GetPaymentProductConfigurationQueryError
	>(
		createPaymentProductConfigurationQuery(
			merchantID,
			sortedProgramIDs,
			programIDs,
			GetPaymentProductConfigurationState.LAPSED,
			enabled,
		),
	);

	// Autofetch next page
	useEffect(() => {
		if (!isFetchingActiveNextPage && hasActiveNextPage) {
			fetchActiveNextPage();
		}
	}, [isFetchingActiveNextPage, fetchActiveNextPage, hasActiveNextPage]);

	useEffect(() => {
		if (!isFetchingLapsedNextPage && hasLapsedNextPage) {
			fetchLapsedNextPage();
		}
	}, [isFetchingLapsedNextPage, fetchLapsedNextPage, hasLapsedNextPage]);

	// Combine all items from ACTIVE and LAPSED
	const items = useMemo(() => {
		if (!activeData?.pages && !lapsedData?.pages) {
			return [];
		}

		const activeItems = activeData?.pages?.flatMap((page) => page.items ?? []) ?? [];
		const lapsedItems = lapsedData?.pages?.flatMap((page) => page.items ?? []) ?? [];
		const combined = [...activeItems, ...lapsedItems];
		return combined;
	}, [activeData?.pages, lapsedData?.pages]);

	const isLoading =
		isLoadingActive ||
		isLoadingLapsed ||
		isFetchingActiveNextPage ||
		isFetchingLapsedNextPage ||
		!!hasActiveNextPage ||
		!!hasLapsedNextPage;

	return { items, isLoading };
};
